import { getAuth } from "firebase/auth";
import { consola } from "consola";

const API_BASE_URL =
  // eslint-disable-next-line no-undef
  (import.meta as any).env?.VITE_API_URL || "https://app.mighty45.com";

/**
 * Get a fresh Firebase ID token for the currently signed-in coach.
 */
async function getIdToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");
  return user.getIdToken();
}

/**
 * Send a text message to a group via the atomic server endpoint.
 *
 * The endpoint writes the message document and updates the group's
 * lastMessage metadata in a single Firestore batch, making it crash-safe
 * and consistent. This replaces the read-then-write pattern in MessageData.ts
 * that left the conversation list showing stale data when the second write failed.
 */
export async function sendMessageApi({
  groupId,
  message,
  appDestination = "messaging",
}: {
  groupId: string;
  message: string;
  appDestination?: string;
}): Promise<{ messageId: string }> {
  if (!groupId || !message || message.length === 0) {
    throw new Error("sendMessageApi: groupId and message are required");
  }

  const token = await getIdToken();

  const response = await fetch(`${API_BASE_URL}/api/v1/message/send`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ groupId, message, appDestination }),
  });

  let responseData: any = {};
  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    try {
      responseData = await response.json();
    } catch {
      // ignore parse errors on error responses
    }
  }

  if (response.ok) {
    consola.debug("messagingApi: message sent", { groupId });
    return responseData.data;
  }

  const errorMsg =
    responseData.error || responseData.message || `HTTP ${response.status}`;
  consola.error("messagingApi: send failed", { status: response.status, errorMsg });
  throw new Error(errorMsg);
}
